import apiClient from './apiClient';

type Envelope<T> = { success?: boolean; data?: T };
const unwrap = <T>(response: any): T => {
  const body = response?.data ?? response;
  return Object.prototype.hasOwnProperty.call(body || {}, 'data') ? body.data as T : body as T;
};

export type EquipmentStatus = 'available' | 'allocated' | 'maintenance' | 'out_of_service';

export interface EquipmentAsset {
  id: string;
  assetCode: string;
  resourceId: string | null;
  serialNumber: string | null;
  status: EquipmentStatus;
  currentLocationId: string | null;
  maintenanceDueDate: string | null;
  ownership: 'owned' | 'rented';
  notes: string | null;
  resource?: { id: string; resourceCode: string; name: string; resourceType: string };
  currentLocation?: { id: string; locationCode: string; name: string };
  allocations?: EquipmentAllocation[];
}

export interface EquipmentAllocation {
  id: string;
  assetId: string;
  taskId: string;
  allocatedFrom: string;
  allocatedTo: string;
  releasedAt: string | null;
  asset?: EquipmentAsset;
  task?: { id: string; taskCode: string; name: string };
}

export interface EquipmentDashboard {
  assets: EquipmentAsset[];
  currentAllocations: EquipmentAllocation[];
  maintenanceDue: EquipmentAsset[];
  summary: {
    totalAssets: number;
    available: number;
    allocated: number;
    maintenance: number;
    outOfService: number;
    maintenanceDue: number;
    utilizationPercent: number;
  };
}

class EquipmentService {
  async getDashboard(projectId?: string): Promise<EquipmentDashboard> {
    const response = await apiClient.get<Envelope<EquipmentDashboard>>('/equipment/dashboard', {
      params: projectId ? { projectId } : undefined,
    });
    return unwrap<EquipmentDashboard>(response);
  }

  async createAsset(input: Record<string, any>): Promise<EquipmentAsset> {
    const response = await apiClient.post<Envelope<EquipmentAsset>>('/equipment/assets', input);
    return unwrap<EquipmentAsset>(response);
  }

  async allocateAsset(input: Record<string, any>): Promise<EquipmentAllocation> {
    const response = await apiClient.post<Envelope<EquipmentAllocation>>('/equipment/allocations', input);
    return unwrap<EquipmentAllocation>(response);
  }

  async releaseAllocation(id: string): Promise<EquipmentAllocation> {
    const response = await apiClient.delete<Envelope<EquipmentAllocation>>(`/equipment/allocations/${id}`);
    return unwrap<EquipmentAllocation>(response);
  }
}

export default new EquipmentService();
